// utils/imageNormalize.js
//
// Client-side avatar image normalization. The server is deliberately strict
// (JPEG/PNG/WebP only — stock sharp/libvips can't decode HEIC, and widening
// the allowlist widens the attack surface), so iPhone HEIC photos and
// oversized picks are handled HERE: the browser decodes them (Safari decodes
// HEIC natively — the platform the format comes from) and re-encodes a
// downscaled JPEG. Allowlisted files under the cap pass through untouched.

export const AVATAR_MIMES = ['image/jpeg', 'image/png', 'image/webp'];
export const AVATAR_MAX_MB = 10;
const AVATAR_MAX_BYTES = AVATAR_MAX_MB * 1024 * 1024;

// Avatar variants top out at 256px; 1024 keeps generous headroom for the
// stored original while shrinking a 12MP HEIC to well under the cap.
const TARGET_EDGE = 1024;

export class UnsupportedImageError extends Error {
	constructor() { super('UNSUPPORTED_IMAGE'); this.code = 'UNSUPPORTED_IMAGE'; }
}
export class ImageTooLargeError extends Error {
	constructor() { super('IMAGE_TOO_LARGE'); this.code = 'IMAGE_TOO_LARGE'; }
}

/**
 * Detect the REAL container format from magic bytes. file.type is a label
 * the OS picked and is not trustworthy — iOS Safari hands over HEIC photo
 * bytes labeled image/jpeg, which sailed past a type-based passthrough and
 * died in the server's byte-level decode. Returns 'jpeg' | 'png' | 'webp' |
 * 'heif' | 'unknown'.
 */
async function readHeadBytes(file, length) {
	const blob = file.slice(0, length);
	if (typeof blob.arrayBuffer === 'function') {
		return new Uint8Array(await blob.arrayBuffer());
	}
	// FileReader fallback (older engines, jsdom in tests).
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(new Uint8Array(reader.result));
		reader.onerror = () => reject(reader.error);
		reader.readAsArrayBuffer(blob);
	});
}

export async function sniffImageFormat(file) {
	let bytes;
	try {
		bytes = await readHeadBytes(file, 16);
	} catch {
		return 'unknown';
	}
	const ascii = (start, end) => String.fromCharCode(...bytes.slice(start, end));
	if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
	if (bytes[0] === 0x89 && ascii(1, 4) === 'PNG') return 'png';
	if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'webp';
	if (ascii(4, 8) === 'ftyp') {
		// HEIF family brands (heic/heix/hevc/mif1/...) — includes AVIF; all
		// need the browser transcode, none are server-decodable.
		return 'heif';
	}
	return 'unknown';
}

async function decodeImage(file) {
	// createImageBitmap is the fast path (and applies EXIF orientation in
	// modern browsers); fall back to an <img> decode where it's missing or
	// refuses the format.
	if (typeof createImageBitmap === 'function') {
		try {
			// Ask for EXIF orientation explicitly: engines that default to
			// ignoring it (Safari <17.4, older Firefox) would otherwise bake
			// sideways pixels into the transcode — and Safari is exactly
			// where the EXIF-carrying HEIC picks come from. Engines without
			// the options dictionary just ignore it.
			return await createImageBitmap(file, { imageOrientation: 'from-image' });
		} catch {
			// fall through to the <img> path (which applies EXIF natively)
		}
	}
	const url = URL.createObjectURL(file);
	try {
		const img = new Image();
		img.src = url;
		if (img.decode) await img.decode();
		else await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; });
		return img;
	} finally {
		// Safe post-decode: the pixels live in the element, not the URL.
		URL.revokeObjectURL(url);
	}
}

/**
 * Returns a File ready for the avatar upload: the original when it's already
 * an allowlisted type under the size cap, otherwise a downscaled JPEG
 * re-encode. Throws UnsupportedImageError when the browser can't decode the
 * pick (not an image, or a format this browser lacks — e.g. HEIC outside
 * Safari), ImageTooLargeError when even the re-encode busts the cap.
 */
export async function normalizeAvatarImage(file) {
	// Passthrough is decided by the BYTES, never the label.
	const format = await sniffImageFormat(file);
	if (['jpeg', 'png', 'webp'].includes(format) && file.size <= AVATAR_MAX_BYTES) {
		const realType = `image/${format}`;
		if (file.type === realType) return file;
		// Right bytes, wrong label (or none): rewrap so the multipart part
		// declares the type the server's allowlist checks.
		return new File([file], file.name || 'avatar', { type: realType });
	}

	let source;
	try {
		source = await decodeImage(file);
	} catch {
		throw new UnsupportedImageError();
	}

	let canvas;
	try {
		const width = source.width || source.naturalWidth;
		const height = source.height || source.naturalHeight;
		if (!width || !height) throw new UnsupportedImageError();

		const scale = Math.min(1, TARGET_EDGE / Math.max(width, height));
		canvas = document.createElement('canvas');
		canvas.width = Math.max(1, Math.round(width * scale));
		canvas.height = Math.max(1, Math.round(height * scale));
		const context = canvas.getContext('2d');
		if (!context) throw new UnsupportedImageError();
		// JPEG has no alpha: composite transparency over white, not the
		// canvas's default transparent-black (a transparent PNG logo would
		// otherwise land on a solid black square).
		context.fillStyle = '#ffffff';
		context.fillRect(0, 0, canvas.width, canvas.height);
		context.drawImage(source, 0, 0, canvas.width, canvas.height);
	} finally {
		// Release the decoded pixel buffer on every path, not just success.
		if (typeof source.close === 'function') source.close();
	}

	const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
	if (!blob) throw new UnsupportedImageError();
	if (blob.size > AVATAR_MAX_BYTES) throw new ImageTooLargeError();

	const baseName = (file.name || 'avatar').replace(/\.[^.]+$/, '');
	return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}
